import { useEffect, useMemo, useRef, useState, useId } from "react";
import * as XLSX from "xlsx";
import { importProducts } from "../api/products";
import { fetchCategories, createCategory } from "../api/categories";
import Icon from "../components/Icon";

const REQUIRED_FIELDS = [
  { key: "name", label: "Product name" },
  { key: "refCode", label: "Ref code", templateHeader: "RefCode" },
  { key: "quantityOnHand", label: "Quantity", templateHeader: "Quantity" },
  { key: "barcode", label: "Barcode" },
];

const OPTIONAL_FIELDS = [
  { key: "defaultPrice", label: "Price", templateHeader: "Price" },
  { key: "costPrice", label: "Cost", templateHeader: "Cost" },
  { key: "expiryDate", label: "Expiry date" },
  { key: "supplierName", label: "Supplier name" },
  { key: "categoryNames", label: "Category", templateHeader: "Category" },
  { key: "reorderLevel", label: "Stock alert", templateHeader: "StockAlert" },
  { key: "zone", label: "Zone", templateHeader: "Zone" },
  { key: "description", label: "Description" },
];

const HEADER_ALIASES = {
  name: "name",
  product: "name",
  productname: "name",
  refcode: "refCode",
  ref: "refCode",
  sku: "refCode",
  itemcode: "refCode",
  barcode: "barcode",
  upc: "barcode",
  ean: "barcode",
  quantity: "quantityOnHand",
  qty: "quantityOnHand",
  quantityonhand: "quantityOnHand",
  qtyonhand: "quantityOnHand",
  stock: "quantityOnHand",
  defaultprice: "defaultPrice",
  sellprice: "defaultPrice",
  price: "defaultPrice",
  costprice: "costPrice",
  cost: "costPrice",
  expiry: "expiryDate",
  expirydate: "expiryDate",
  expirationdate: "expiryDate",
  supplier: "supplierName",
  suppliername: "supplierName",
  supplierlabel: "supplierName",
  supplierid: "supplierId",
  category: "categoryNames",
  categories: "categoryNames",
  categoryname: "categoryNames",
  categorynames: "categoryNames",
  categorylabels: "categoryNames",
  categoryid: "categoryId",
  description: "description",
  details: "description",
  reorderlevel: "reorderLevel",
  reorder: "reorderLevel",
  stockalert: "reorderLevel",
  status: "status",
  zone: "zone",
  location: "zone",
  warehousezone: "zone",
  aisle: "zone",
};

const MAX_ROWS = 2000;
const REQUIRED_KEYS = REQUIRED_FIELDS.map((item) => item.key);
const TEMPLATE_COLUMNS = [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS];

const normalizeHeader = (header) => header?.toString?.().trim().toLowerCase().replace(/[^a-z0-9]/g, "") || "";
const mapHeader = (header) => HEADER_ALIASES[normalizeHeader(header)] || null;
const isEmpty = (value) => value === undefined || value === null || (typeof value === "string" ? value.trim() === "" : false);
const toDisplay = (value) => (value === undefined || value === null || value === "" ? "—" : value);
const normalizeCategoryKey = (value) =>
  value?.toString?.().trim().toLowerCase().replace(/\s+/g, "") || "";
const normalizeLookupKey = (value) =>
  value?.toString?.().trim().toLowerCase().replace(/\s+/g, "") || "";
const splitCategoryNames = (value) => {
  if (!value && value !== 0) return [];
  if (Array.isArray(value)) {
    return value
      .map((entry) => entry?.toString?.().trim())
      .filter(Boolean);
  }
  const raw = value?.toString?.() || "";
  if (!raw.trim()) return [];
  return raw
    .split(/[|,;/]/)
    .map((part) => part.trim())
    .filter(Boolean);
};

const normalizeDateValue = (value) => {
  if (!value) return value;
  const raw = value.toString().trim();
  if (!raw) return raw;
  const slashPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  const match = raw.match(slashPattern);
  if (!match) return raw;
  const [, dayStr, monthStr, yearStr] = match;
  const day = Number(dayStr);
  const month = Number(monthStr);
  const year = Number(yearStr);
  if (!day || !month || !year || day > 31 || month > 12) return raw;
  const isoDay = String(day).padStart(2, "0");
  const isoMonth = String(month).padStart(2, "0");
  return `${year}-${isoMonth}-${isoDay}`;
};

function ProductImportPage({ onClose, onImported, suppliers = [], onRequestSupplierCreate }) {
  const [fileMeta, setFileMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [detectedColumns, setDetectedColumns] = useState([]);
  const [parsingError, setParsingError] = useState("");
  const [backendErrors, setBackendErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [categoryLookup, setCategoryLookup] = useState(() => new Map());
  const [categoryPrompts, setCategoryPrompts] = useState([]);
  const [supplierLookup, setSupplierLookup] = useState(() => new Map());
  const [supplierPrompts, setSupplierPrompts] = useState([]);
  const [confirmPromptError, setConfirmPromptError] = useState("");
  const fileInputRef = useRef(null);
  const fileInputId = useId();

  const reset = () => {
    setFileMeta(null);
    setRows([]);
    setDetectedColumns([]);
    setParsingError("");
    setBackendErrors([]);
    setSuccessMessage("");
    setImporting(false);
    setCategoryPrompts([]);
    setSupplierPrompts([]);
    setConfirmPromptError("");
  };

  useEffect(() => {
    let active = true;
    const loadCategories = async () => {
      try {
        const categories = await fetchCategories();
        if (!active) return;
        const map = new Map();
        (categories || []).forEach((cat) => {
          const key = normalizeCategoryKey(cat?.name);
          if (!key) return;
          map.set(key, { id: cat?.id || cat?._id || "", name: cat?.name || "" });
        });
        setCategoryLookup(map);
      } catch (err) {
        if (active) {
          console.error("Unable to load categories", err);
        }
      }
    };
    loadCategories();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const map = new Map();
    (suppliers || []).forEach((sup) => {
      const key = normalizeLookupKey(sup?.name);
      if (!key || map.has(key)) return;
      map.set(key, { id: sup?.id || sup?._id || sup?.value || "", name: sup?.name || "" });
    });
    setSupplierLookup(map);
  }, [suppliers]);

  const downloadTemplate = () => {
    const headers = TEMPLATE_COLUMNS.map((item) => item.templateHeader || item.key);
    const sampleRows = [
      {
        name: "Paracetamol 500mg",
        refCode: "MED-500",
        barcode: "2000000000001",
        quantityOnHand: "150",
        defaultPrice: "3.5",
        costPrice: "0.9",
        supplierName: "Acme Pharma",
        categoryNames: "Pain Relief|Tablets",
        reorderLevel: "25",
        zone: "A1",
        description: "Pain relief tablets",
      },
      {
        name: "Vitamin D3 1000IU",
        refCode: "VIT-D3",
        barcode: "2000000000002",
        quantityOnHand: "80",
        defaultPrice: "6.25",
        costPrice: "2.1",
        supplierName: "Sun Nutrition",
        categoryNames: "Vitamins",
        expiryDate: "2025-06-30",
        reorderLevel: "15",
        zone: "B3",
        description: "Bone health supplement",
      },
    ];
    const csvRows = sampleRows.map((row) => TEMPLATE_COLUMNS.map((col) => row[col.key] ?? "").join(","));
    const csv = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "product-import-template.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const parseSheet = (sheet) => {
    const sheetRows = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
    const mappedRows = [];
    const columnSet = new Set();

    sheetRows.forEach((row) => {
      const mapped = {};
      Object.entries(row).forEach(([header, value]) => {
        const key = mapHeader(header);
        if (!key) return;
        columnSet.add(key);
        const cleanValue = typeof value === "string" ? value.trim() : value;
        mapped[key] = cleanValue;
      });

      const hasContent = Object.values(mapped).some((value) => !isEmpty(value) || value === 0);
      if (hasContent) {
        mappedRows.push(mapped);
      }
    });

    return { rows: mappedRows, columns: Array.from(columnSet) };
  };

  const handleFile = async (file) => {
    if (!file) return;

    const fileName = file.name || "import.csv";
    const allowed = [".csv", ".xlsx", ".xls"].some((ext) => fileName.toLowerCase().endsWith(ext));
    if (!allowed) {
      setParsingError("Please upload a CSV or Excel file (.csv, .xlsx, .xls).");
      return;
    }

    setParsingError("");
    setBackendErrors([]);
    setSuccessMessage("");

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const firstSheetName = workbook.SheetNames?.[0];
      if (!firstSheetName) {
        throw new Error("This file is empty.");
      }

      const sheet = workbook.Sheets[firstSheetName];
      const { rows: parsedRows, columns } = parseSheet(sheet);

      if (!parsedRows.length) {
        throw new Error("No data rows found. Please check your headers and rows.");
      }

      if (parsedRows.length > MAX_ROWS) {
        throw new Error(`Please import ${MAX_ROWS} rows or fewer at a time.`);
      }

      setRows(parsedRows);
      setDetectedColumns(columns);
      setFileMeta({
        name: fileName,
        size: file.size || 0,
        rowCount: parsedRows.length,
      });
    } catch (err) {
      console.error("Unable to parse import file", err);
      setParsingError(err?.message || "Unable to read that file. Please try a different one.");
    }
  };

  const handleFileChange = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      handleFile(file);
    }
    if (event?.target) {
      event.target.value = "";
    }
  };

  const openFilePicker = () => {
    const input = fileInputRef.current;
    if (!input) return;
    // Prefer showPicker when available (some browsers block click on hidden inputs).
    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }
    input.click();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDropzoneKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openFilePicker();
    }
  };

  useEffect(() => {
    if (!rows.length) {
      setCategoryPrompts([]);
      setConfirmPromptError("");
      return;
    }
    setCategoryPrompts((prevPrompts) => {
      const prevMap = new Map(prevPrompts.map((prompt) => [prompt.key, prompt]));
      const required = new Map();
      rows.forEach((row) => {
        const names = splitCategoryNames(row.categoryNames);
        names.forEach((name) => {
          const key = normalizeCategoryKey(name);
          if (!key || categoryLookup.has(key)) return;
          if (!required.has(key)) {
            required.set(key, prevMap.get(key)?.name || name);
          }
        });
      });
      const nextPrompts = [];
      required.forEach((name, key) => {
        const existing = prevMap.get(key);
        nextPrompts.push(existing ? { ...existing, name: existing.name || name } : { key, name, status: "pending", error: "" });
      });
      if (!nextPrompts.length) {
        setConfirmPromptError("");
      }
      return nextPrompts;
    });
  }, [rows, categoryLookup]);

  useEffect(() => {
    if (!rows.length) {
      setSupplierPrompts([]);
      return;
    }
    setSupplierPrompts((prevPrompts) => {
      const prevMap = new Map(prevPrompts.map((prompt) => [prompt.key, prompt]));
      const required = new Map();
      rows.forEach((row) => {
        const supplierName = row.supplierName?.toString?.().trim();
        if (!supplierName) return;
        const key = normalizeLookupKey(supplierName);
        if (!key || supplierLookup.has(key)) return;
        if (!required.has(key)) {
          required.set(key, prevMap.get(key)?.name || supplierName);
        }
      });
      const nextPrompts = [];
      required.forEach((name, key) => {
        const existing = prevMap.get(key);
        nextPrompts.push(existing ? { ...existing, name } : { key, name, status: "pending", error: "" });
      });
      return nextPrompts;
    });
  }, [rows, supplierLookup]);

  const handleSkipCategoryPrompt = (key) => {
    setConfirmPromptError("");
    setRows((prevRows) =>
      prevRows.map((row) => {
        const names = splitCategoryNames(row.categoryNames);
        if (!names.length) return row;
        const filtered = names.filter((name) => normalizeCategoryKey(name) !== key);
        if (filtered.length === names.length) return row;
        const nextRow = { ...row };
        if (filtered.length) {
          nextRow.categoryNames = filtered.join("|");
        } else {
          delete nextRow.categoryNames;
        }
        return nextRow;
      })
    );
    setCategoryPrompts((prev) => prev.filter((prompt) => prompt.key !== key));
  };

  const handleCreateCategoryPrompt = async (prompt) => {
    setConfirmPromptError("");
    setCategoryPrompts((prev) =>
      prev.map((item) => (item.key === prompt.key ? { ...item, status: "creating", error: "" } : item))
    );
    try {
      const category = await createCategory({ name: prompt.name });
      const resolvedName = category?.name || prompt.name;
      const resolvedKey = normalizeCategoryKey(resolvedName) || prompt.key;
      setCategoryLookup((prev) => {
        const next = new Map(prev);
        next.set(resolvedKey, { id: category?.id || category?._id || "", name: resolvedName });
        return next;
      });
    } catch (err) {
      setCategoryPrompts((prev) =>
        prev.map((item) =>
          item.key === prompt.key ? { ...item, status: "pending", error: err?.message || "Unable to create category." } : item
        )
      );
    }
  };

  const handleSkipSupplierPrompt = (key) => {
    setConfirmPromptError("");
    setRows((prevRows) =>
      prevRows.map((row) => {
        const supplierName = row.supplierName?.toString?.().trim();
        if (!supplierName) return row;
        if (normalizeLookupKey(supplierName) !== key) return row;
        const nextRow = { ...row };
        delete nextRow.supplierName;
        delete nextRow.supplierId;
        return nextRow;
      })
    );
    setSupplierPrompts((prev) => prev.filter((prompt) => prompt.key !== key));
  };

  const handleCreateSupplierPrompt = (prompt) => {
    setConfirmPromptError("");
    onRequestSupplierCreate?.(prompt.name);
  };

  const validation = useMemo(() => {
    if (!rows.length) {
      return {
        missingColumns: [],
        rowIssues: [],
        blocking: true,
      };
    }

    const missingColumns = REQUIRED_KEYS.filter((key) => !detectedColumns.includes(key));
    const rowIssues = [];
    const seenRefCode = new Set();
    const seenBarcode = new Set();
    const numberFields = ["quantityOnHand", "defaultPrice", "costPrice"];

    rows.forEach((row, idx) => {
      const errors = [];

      REQUIRED_FIELDS.forEach((field) => {
        const value = row[field.key];
        if (field.key === "barcode") return;
        if (isEmpty(value) && value !== 0) {
          errors.push(`${field.label} is required`);
        }
      });

      numberFields.forEach((field) => {
        const raw = row[field];
        if (isEmpty(raw)) return;
        const numeric = Number(raw);
        if (Number.isNaN(numeric)) {
          const label = OPTIONAL_FIELDS.find((item) => item.key === field)?.label || field;
          errors.push(`${label} must be a number`);
        }
      });

      const refCode = row.refCode?.toString().trim();
      if (refCode) {
        if (seenRefCode.has(refCode)) errors.push("Duplicate ref code in file");
        seenRefCode.add(refCode);
      }

      const barcode = row.barcode?.toString().trim();
      if (barcode) {
        if (!/^[0-9]+$/.test(barcode)) {
          errors.push("Barcode must contain numbers only");
        }
        if (seenBarcode.has(barcode)) errors.push("Duplicate barcode in file");
        seenBarcode.add(barcode);
      }

      if (errors.length) {
        rowIssues.push({ index: idx, errors });
      }
    });

    return {
      missingColumns,
      rowIssues,
      blocking: Boolean(missingColumns.length || rowIssues.length),
    };
  }, [rows, detectedColumns]);

  const handleImport = async () => {
    const pendingGroups = [];
    if (categoryPrompts.length) pendingGroups.push("categories");
    if (supplierPrompts.length) pendingGroups.push("suppliers");
    if (pendingGroups.length) {
      const label =
        pendingGroups.length > 1
          ? `${pendingGroups.slice(0, -1).join(", ")} and ${pendingGroups[pendingGroups.length - 1]}`
          : pendingGroups[0];
      setConfirmPromptError(`Please confirm the highlighted ${label} before importing.`);
      return;
    }
    setConfirmPromptError("");
    if (!rows.length || validation.blocking || importing) return;
    setImporting(true);
    setBackendErrors([]);
    setParsingError("");
    setSuccessMessage("");

    try {
      const payload = rows.map((row) =>
        Object.fromEntries(
          Object.entries(row).map(([key, value]) => {
            if (key === "expiryDate") {
              return [key, normalizeDateValue(value)];
            }
            return [key, typeof value === "string" ? value.trim() : value];
          })
        )
      );
      const response = await importProducts({ products: payload });
      const inserted = response?.inserted ?? payload.length;
      const message = `Imported ${inserted} product${inserted === 1 ? "" : "s"}.`;
      setSuccessMessage(message);
      onImported?.({ inserted, fileName: fileMeta?.name });
    } catch (err) {
      const apiErrors = err?.data?.errors;
      if (Array.isArray(apiErrors)) {
        setBackendErrors(apiErrors);
      }
      setParsingError(err?.message || "Import failed. Please try again.");
    } finally {
      setImporting(false);
    }
  };

  const readyCount = rows.length;
  const missing = validation.missingColumns;
  const showBackendIssues = Array.isArray(backendErrors) && backendErrors.length;
  const previewColumns = useMemo(() => {
    if (detectedColumns.length) return detectedColumns;
    return TEMPLATE_COLUMNS.map((col) => col.key);
  }, [detectedColumns]);

  return (
    <div className="import-page">
      <div className="import-page__grid">
        <section className="import-page__card import-panel" aria-label="Import products panel">
        <div className="import-page__header">
          <div>
            <h2 className="import-page__title">Import products</h2>
            <p className="muted small">Upload a CSV or Excel file to create products in bulk. Required columns are name, ref code, quantity, and barcode (auto-generated if missing).</p>
          </div>
          {onClose ? (
            <button className="ghost-btn subtle" type="button" onClick={onClose}>
              Exit import
            </button>
          ) : null}
        </div>

        <div className="import-actions">
          <button className="ghost-btn" type="button" onClick={downloadTemplate}>
            <Icon name="doc" />
            Download sample CSV
          </button>
          <div className="muted extra-small">
            Accepted: .csv, .xlsx, .xls — up to {MAX_ROWS} rows.
          </div>
        </div>

        <div className="import-dropzone-wrapper" onDrop={handleDrop} onDragOver={handleDragOver}>
          <input
            id={fileInputId}
            type="file"
            accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            ref={fileInputRef}
            className="import-dropzone__input"
            aria-label="Select a CSV or Excel file to import"
            onChange={handleFileChange}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          />
          <label
            className="import-dropzone"
            role="button"
            tabIndex={0}
            htmlFor={fileInputId}
            onKeyDown={handleDropzoneKeyDown}
          >
            <div className="import-dropzone__icon">
              <Icon name="import" />
            </div>
            <div>
              <div className="import-dropzone__title">Drag & drop file here</div>
              <div className="muted small">or click to browse</div>
            </div>
            <div className="pill subtle">CSV / Excel</div>
          </label>
        </div>

        {fileMeta ? (
          <div className="import-meta">
            <div>
              <div className="muted extra-small">File</div>
              <div className="meta-strong">{fileMeta.name}</div>
              <div className="muted extra-small">{(fileMeta.size / 1024).toFixed(1)} KB</div>
            </div>
            <div>
              <div className="muted extra-small">Rows detected</div>
              <div className="meta-strong">{fileMeta.rowCount}</div>
            </div>
            <div>
              <div className="muted extra-small">Columns</div>
              <div className="meta-strong">{detectedColumns.length || "—"}</div>
            </div>
            <button className="ghost-btn subtle" type="button" onClick={reset}>
              Clear file
            </button>
          </div>
        ) : null}

        {missing.length ? (
          <div className="inline-alert error">
            <span>Missing columns:</span>
            <div className="pill-row">
              {missing.map((key) => {
                const label = REQUIRED_FIELDS.find((item) => item.key === key)?.label || key;
                return (
                  <span key={key} className="pill danger">
                    {label}
                  </span>
                );
              })}
            </div>
          </div>
        ) : null}

        {categoryPrompts.length ? (
          <div className="inline-alert warn">
            <div>
              <div className="meta-strong">Action required: confirm categories</div>
              <div className="muted extra-small">
              These categories were not found in your catalog. Decide whether to create them or skip them before importing.
            </div>
          </div>
          <div className="import-issues">
            {categoryPrompts.map((prompt) => (
              <div key={prompt.key} className="issue-row">
                <div className="pill warn">{prompt.name}</div>
                <div className="muted extra-small">Create this category for import?</div>
                <div className="confirm-actions">
                  <button
                    className="primary-btn small"
                    type="button"
                    onClick={() => handleCreateCategoryPrompt(prompt)}
                    disabled={prompt.status === "creating"}
                  >
                    {prompt.status === "creating" ? "Creating..." : "Create"}
                  </button>
                  <button className="ghost-btn small subtle" type="button" onClick={() => handleSkipCategoryPrompt(prompt.key)}>
                    Skip
                  </button>
                </div>
                {prompt.error ? <div className="muted extra-small danger">{prompt.error}</div> : null}
              </div>
            ))}
          </div>
        </div>
        ) : null}

        {parsingError ? (
          <div className="inline-alert error">
            <span>{parsingError}</span>
          </div>
        ) : null}

        {supplierPrompts.length ? (
          <div className="inline-alert warn">
            <div>
              <div className="meta-strong">Action required: confirm suppliers</div>
              <div className="muted extra-small">
                These suppliers aren't in your directory. Create them now (opens the supplier form) or skip to import without a supplier link.
              </div>
            </div>
            <div className="import-issues">
              {supplierPrompts.map((prompt) => (
                <div key={prompt.key} className="issue-row">
                  <div className="pill warn">{prompt.name}</div>
                  <div className="muted extra-small">Create this supplier now?</div>
                  <div className="confirm-actions">
                    <button className="primary-btn small" type="button" onClick={() => handleCreateSupplierPrompt(prompt)}>
                      Create supplier
                    </button>
                    <button className="ghost-btn small subtle" type="button" onClick={() => handleSkipSupplierPrompt(prompt.key)}>
                      Skip supplier
                    </button>
                  </div>
                  {prompt.error ? <div className="muted extra-small danger">{prompt.error}</div> : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {validation.rowIssues.length ? (
          <div className="inline-alert warn">
            <div>
              <div className="meta-strong">Fix these rows before importing</div>
              <div className="muted extra-small">Showing the first few issues.</div>
            </div>
            <div className="import-issues">
              {validation.rowIssues.slice(0, 4).map((issue) => (
                <div key={issue.index} className="issue-row">
                  <div className="pill warn">Row {issue.index + 2}</div>
                  <div className="muted extra-small">{issue.errors.join("; ")}</div>
                </div>
              ))}
              {validation.rowIssues.length > 4 ? (
                <div className="muted extra-small">+{validation.rowIssues.length - 4} more issue(s)</div>
              ) : null}
            </div>
          </div>
        ) : null}

        {showBackendIssues ? (
          <div className="inline-alert error">
            <div>
              <div className="meta-strong">Import blocked by server</div>
              <div className="muted extra-small">Update the file and try again.</div>
            </div>
            <div className="import-issues">
              {backendErrors.slice(0, 5).map((issue, idx) => (
                <div key={`${issue.index}-${idx}`} className="issue-row">
                  <div className="pill danger">Row {Number(issue.index) + 2}</div>
                  <div className="muted extra-small">{Array.isArray(issue.errors) ? issue.errors.join("; ") : issue.errors}</div>
                </div>
              ))}
              {backendErrors.length > 5 ? (
                <div className="muted extra-small">+{backendErrors.length - 5} more issue(s)</div>
              ) : null}
            </div>
          </div>
        ) : null}

        {successMessage ? (
          <div className="inline-alert success">
            <span>{successMessage}</span>
          </div>
        ) : null}

        {confirmPromptError ? (
          <div className="inline-alert error">
            <span>{confirmPromptError}</span>
          </div>
        ) : null}

        {importing ? (
          <div className="import-progress" role="status" aria-live="assertive">
            <div className="import-progress__top">
              <div>
                <div className="meta-strong">Working on your import</div>
                <div className="muted extra-small">
                  Processing {readyCount.toLocaleString()} product{readyCount === 1 ? "" : "s"}. Keep this tab open.
                </div>
              </div>
              <div className="pill subtle">
                <span className="import-progress__dot" aria-hidden="true" />
                Importing
              </div>
            </div>
            <div className="import-progress__bar" aria-hidden="true">
              <div className="import-progress__bar-fill" />
            </div>
          </div>
        ) : null}

        <footer className="import-page__footer">
          <button className="primary-btn wide" type="button" onClick={handleImport} disabled={!rows.length || validation.blocking || importing}>
            {importing ? "Importing..." : readyCount ? `Import ${readyCount} products` : "Import"}
          </button>
          {onClose ? (
            <button className="ghost-btn subtle wide" type="button" onClick={onClose} disabled={importing}>
              Cancel
            </button>
          ) : null}
        </footer>
      </section>

      <section className="import-page__card import-preview" aria-label="Preview uploaded products">
        <div className="import-preview__head">
          <div>
            <div className="meta-strong">File preview</div>
            <div className="muted extra-small">
              {readyCount
                ? `Showing all ${readyCount} row${readyCount === 1 ? "" : "s"} • ${previewColumns.length} column${previewColumns.length === 1 ? "" : "s"}. Scroll to explore.`
                : "Upload a file to see all rows and columns."}
            </div>
          </div>
          {readyCount ? <div className="pill subtle">{readyCount.toLocaleString()} rows</div> : null}
        </div>
        {readyCount ? (
          <div className="import-preview__table import-preview__table--scroll">
            <table>
              <thead>
                <tr>
                  {previewColumns.map((field) => (
                    <th key={field}>
                      {REQUIRED_FIELDS.find((item) => item.key === field)?.label ||
                        OPTIONAL_FIELDS.find((item) => item.key === field)?.label ||
                        field}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx}>
                    {previewColumns.map((field) => (
                      <td key={`${idx}-${field}`}>{toDisplay(row[field])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="import-preview__empty">
            <p className="muted">No data to show yet. Upload a CSV or Excel file to preview every row.</p>
          </div>
        )}
      </section>
    </div>
  </div>
);
}

export default ProductImportPage;
